import type { RoleId } from '@onuw/roles-data'
import type { RoleGuide } from './types'

export const BASE_GUIDES = {
  doppelganger: {
    lead: "She looks at another player's card and becomes that role for the rest of the game: its team, its night action and its way of winning.",
    does: [
      'The Doppelgänger wakes early, right after the [[sentinel]] (with marks in play she wakes at dusk, before the Vampires). She must look at one other player’s card, and from then on her own card is a copy of what that card is: a second [[seer]], a third [[werewolf]], another [[tanner]].',
      'What she copied decides the rest of her night. A role that acts alone she plays at once: the Seer’s look, the [[robber]]’s theft, the [[troublemaker]]’s swap, the [[drunk]]’s trade. A group she joins when it is called: the Werewolves, the [[mason|Masons]], the Vampires, the Aliens. Everything else she plays on her own right after the original: the [[insomniac]], the [[revealer]], the [[curator]] and every role the app reads an instruction to.',
    ],
    rules: [
      'The copy belongs to her card. If the card is moved later, whoever holds it when the night ends is the role she copied.',
      'She becomes what the card is by then. A [[copycat]] that already copied a Werewolf makes her a Werewolf, and she is not told what the Copycat chose. Copying the [[oracle]] or [[nostradamus]] gives her no action of her own: she follows the Oracle’s answer, or joins the team Nostradamus announces.',
      'A Doppelgänger who copied the [[paranormalInvestigator]] and then finds a Werewolf counts as a werewolf in the vote, but she does not wake with the pack.',
      'With her in the deck, the night pauses after every role she might have copied, whether she did or not, so the rhythm of the night gives nothing away.',
      'A Doppelgänger card that reached a player from the center copied nobody: it is a plain Doppelgänger on the village team.',
    ],
    tips: [
      'Your claim has two halves: what you copied, and from whom. The player you copied can tell the table whether your story fits their night.',
      'If you copied a Robber or a Troublemaker, you moved cards before the original did. Work out the order of both moves before you speak.',
      'Copying a Werewolf gives you the pack’s best cover: nobody expects the second claim of an honest role to come from a wolf.',
    ],
    against: [
      'With the Doppelgänger in the deck, two players claiming the same one-of-a-kind role are not automatically lying. Ask which of them looked at the other’s card.',
      'Her copy acts early. A Doppelgänger-Robber may have taken a card that the real Robber then stole again.',
    ],
  },
  werewolf: {
    lead: 'The Werewolves know each other and want one thing from the vote: that no werewolf dies.',
    does: [
      'All the Werewolves wake together and see who the others are. The [[alphaWolf]], the [[mysticWolf]] and the [[giantWolf]] wake with the pack; the [[dreamWolf]] stays asleep, but the pack sees who it is.',
      'A Werewolf who wakes alone may look at one card in the center. It is the lone wolf’s best tool: a role that lies in the center is a role nobody can contradict.',
    ],
    rules: [
      'The Werewolves win if no werewolf dies. If the [[tanner]] dies they lose, even with every wolf alive.',
      'What counts is the card you hold when the night ends. A Werewolf robbed by the [[robber]] is on the village team, and the Robber is the wolf.',
      'A deck holds up to two Werewolf cards. Both may lie in the center: then the village wins only if nobody dies.',
      'A wolf with the Mark of Fear keeps their eyes closed, and the pack does not see them.',
    ],
    tips: [
      'Claim early and claim something specific. A role you saw in the center is the safest; a Villager claim is weak when the deck holds a single Villager.',
      'Do not be the first to defend your partner. Two players vouching for each other out of nowhere is what the village looks for.',
      'If a [[troublemaker]] says they swapped your card, you may no longer be a wolf. Decide whether you believe it before you vote.',
    ],
    against: [
      'Ask everyone what they did and to whom, then look for the two stories that cannot both be true.',
      'Use the claim sheet. A role turns red when more players claim it than the deck has cards of it, and one of those players is lying.',
      'Wolves know more than they should. A player who is certain about a card they had no way to see has given something away.',
    ],
  },
  minion: {
    lead: 'On the Werewolves’ side without being one: the Minion knows the wolves, and the wolves do not know the Minion.',
    does: [
      'The Minion wakes after the Werewolves and sees who holds a werewolf card, the [[dreamWolf]] included. The wolves get no sign that a Minion exists.',
    ],
    rules: [
      'The Minion wins with the Werewolves, and still wins if the Minion is the one who dies.',
      'With no werewolf among the players (every wolf card lies in the center), the Minion wins if any other player dies.',
      'The Minion is not a werewolf. The village gains nothing by killing one, and a [[seer]] who looks at the card sees a Minion.',
    ],
    tips: [
      'Draw the vote onto yourself. A Minion who is voted out has won the game for the wolves.',
      'Claim the role a wolf is being accused by, or accuse a villager with a confident, wrong story.',
      'Do not rush to defend the wolves by name. An eager defender points straight at the pack.',
    ],
    against: [
      'A player who seems to want the vote may be the [[tanner]] or the Minion. Killing either one loses the village the game, so vote for neither on a hunch.',
      'The Minion saw the wolves. Whoever the Minion steers suspicion away from is worth a second look.',
    ],
  },
  mason: {
    lead: 'Two Masons know each other from the start: a small island of trust at a table of liars.',
    does: [
      'The Masons wake together and see each other. A Mason who wakes alone knows the other Mason card lies in the center.',
    ],
    rules: [
      'Masons come as a pair: a deck holds two Mason cards or none.',
      'A Mason with the Mark of Fear keeps their eyes closed, and the other one believes they are alone.',
      'You know who was dealt the other Mason, not who holds it now. Your partner may have been robbed or swapped later in the night.',
    ],
    tips: [
      'Do not claim in the first seconds. Let a wolf claim Mason if they dare, then contradict them together.',
      'A lone Mason knows something too: the other Mason is in the center, so a second Mason claim is a lie (unless the [[doppelganger]] copied you).',
    ],
    against: [
      'Two players confirming each other is also what two Werewolves would do. Check that the Mason story fits the deck and the other claims.',
    ],
  },
  seer: {
    lead: 'The village’s best source of facts: one player’s card, or two cards from the center.',
    does: [
      'The Seer may look at one other player’s card, or at two of the center cards. Looking is optional, and a shielded card cannot be viewed.',
    ],
    rules: [
      'The Seer sees the card as it lies at that moment. The [[robber]], the [[witch]], the [[troublemaker]] and the [[drunk]] all wake later and may move it.',
      'She cannot look at her own card: only the [[insomniac]] learns what she ends the night as.',
    ],
    tips: [
      'A player’s card is a hard fact about one person. Two center cards are two roles nobody holds, which catches anyone who claims them.',
      'Say what you saw before the wolves have agreed on a story, but hold back one detail so that you can test a second Seer claim.',
      'Mind the order. If the Troublemaker later swapped the player you looked at, your fact is about the card, not about the player.',
    ],
    against: [
      'Wolves like to claim Seer because it lets them accuse. Ask a second Seer which card they saw and where: a made-up look often names a card that somebody else can disprove.',
    ],
  },
  robber: {
    lead: 'Steals another player’s card, looks at it, and becomes it.',
    does: [
      'The Robber may swap their card with another player’s card and then look at the new one. The other player now holds the Robber card and does not know it.',
    ],
    rules: [
      'The Robber does not play the stolen role’s night action. Robbing the Seer does not let you look at anything.',
      'Rob a Werewolf and you are the Werewolf: you win with the wolves, and the player you robbed is on the village team.',
      'The Robber wakes before the [[troublemaker]] and the [[drunk]], so the card he looked at can still be swapped away from him.',
      'A shielded card cannot be robbed, and robbing nobody is allowed.',
    ],
    tips: [
      'After robbing a village role, say so early: you know one card for certain, the one in front of you.',
      'After robbing a wolf you have a choice the village hates: claim that you robbed someone harmless.',
      'Name the player you robbed. They can confirm what they were dealt, which makes your claim hard to fake.',
    ],
    against: [
      'A Robber claim gives you two things to test: what the robbed player was dealt, and whether that player’s story from before they knew matches it.',
    ],
  },
  troublemaker: {
    lead: 'Swaps the cards of two other players without looking at them.',
    does: [
      'The Troublemaker may exchange the cards of two other players. Nobody, the Troublemaker included, sees what the cards are.',
    ],
    rules: [
      'She cannot swap her own card, and shielded cards stay where they are.',
      'She wakes after the [[robber]] and before the [[drunk]] and the [[insomniac]]. The Insomniac is the one player who can discover a swap on her own.',
    ],
    tips: [
      'Announce the swap early and watch the two players. A Werewolf who hears they are now a villager often changes their story.',
      'Swapping a suspected wolf with a trusted player moves the wolf card in front of the trusted player. Be clear about who holds what after your swap.',
      'A bluff works too: claiming a swap you did not make can pull a confession out of a nervous wolf.',
    ],
    against: [
      'A wolf who is told "I swapped you" has to decide whether to believe it. If it is true, they now want the wolf card to die, and it lies in front of the other player.',
    ],
  },
  drunk: {
    lead: 'Trades their card for one from the center and never finds out what it was.',
    does: ['The Drunk must swap their card with one of the center cards, without looking at the new one.'],
    rules: [
      'The swap is not optional.',
      'The Drunk wakes after the [[robber]], the [[witch]] and the [[troublemaker]], so the card that goes to the center is whatever lies in front of the Drunk by then.',
      'The Drunk no longer holds the Drunk card: for the vote they are whatever they picked up.',
    ],
    tips: [
      'Say which center card you took. A [[seer]], an [[appSeer]] or a lone wolf who looked there can tell you what you are.',
      'If someone saw a Werewolf where you took your card, you are the wolf now, and you win with the wolves.',
    ],
    against: [
      'Everyone who looked at the center should compare notes with the Drunk. Together they can pin down one more card.',
    ],
  },
  insomniac: {
    lead: 'Wakes last and looks at her own card: the one villager who knows what she ends the night as.',
    does: [
      'At the end of the night the Insomniac looks at the card in front of her. If it is still the Insomniac, nobody moved it; if not, she knows what she became.',
    ],
    rules: [
      'She wakes after every role that moves cards, so what she sees is final.',
      'A shield on her own card blocks her: she cannot look at it.',
      'She learns what she holds, not who moved it.',
    ],
    tips: [
      'An unchanged card disproves anyone who says they robbed or swapped you.',
      'If you woke up as a Werewolf, you are one. The honest thing and the winning thing are no longer the same.',
    ],
    against: [
      'The Insomniac is the check on [[robber]] and [[troublemaker]] stories. A liar who says they moved her card gets caught.',
    ],
  },
  villager: {
    lead: 'No night action and no information, on the team that wins by asking the right questions.',
    does: ['The Villager sleeps through the night.'],
    rules: [
      'A deck holds up to three Villager cards.',
      'A Villager may still end the night as something else, if the [[robber]], the [[troublemaker]] or the [[witch]] moved the card.',
    ],
    tips: [
      'You have nothing to hide, so be the one who asks the questions and keeps track of who said what.',
      'Villager is the wolves’ favourite claim. Count the Villager cards in the deck: one claim too many, and somebody is lying.',
    ],
    against: [
      'No night action can confirm a Villager claim. Weigh it by how many Villager cards the deck holds and how many players claim one.',
    ],
  },
  hunter: {
    lead: 'If the Hunter dies, the player the Hunter voted for dies too.',
    does: ['The Hunter has no night action. The power works in the vote.'],
    rules: [
      'The Hunter’s vote kills only if the Hunter dies. The target then dies whatever their own vote count.',
      'A target who cannot die stays alive: the [[prince]], or a player the [[bodyguard]] voted for.',
      'The power belongs to the card. Whoever holds the Hunter when the night ends has it, and so does the player with the [[curator]]’s Bow of the Hunter.',
      'A Mark of Love chains with it: the Hunter’s target dies, and their lover with them.',
    ],
    tips: [
      'Vote for the player you are most sure is a wolf, not with the crowd. Your vote is the one that still counts if the table turns on you.',
      'A Hunter claim makes the wolves think twice before pushing the vote onto you.',
    ],
    against: [
      'For the wolves a dead Hunter is dangerous only if the Hunter voted for a wolf. Steer the Hunter’s suspicion before you steer the vote.',
    ],
  },
  tanner: {
    lead: 'The Tanner hates his job and wants to die: he wins only if he is voted out.',
    does: ['The Tanner has no night action.'],
    rules: [
      'The Tanner wins if he dies. When he does, the Werewolves, the Vampires and the Aliens cannot win, and the village wins only if one of their targets died as well.',
      'The Tanner is on his own team: he wins with neither the village nor the wolves.',
      'The card decides. The player holding the Tanner when the night ends is the one who wants to die.',
    ],
    tips: [
      'Look guilty, not theatrical. The best Tanner tells a story with one hole in it, the way a wolf under pressure would.',
      'Contradict a player the table trusts, then give ground slowly.',
    ],
    against: [
      'With the Tanner in the deck, a player who is too easy to catch may be the Tanner. Check their story against the others before you vote.',
      'The [[appTanner]] knows who the Tanner is and wants him dead too.',
    ],
  },
} satisfies Partial<Record<RoleId, RoleGuide>>
